import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, FileText, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare, Award, Send, Download, Mail, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstitutionalDashboard() {
  const { user, token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [appsRes, certsRes, msgsRes] = await Promise.all([
        axios.get(`${API}/my-institutional-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my-certificates/institutional`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/portal/messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setApplications(appsRes.data);
      setCertificates(certsRes.data);
      setMessages(msgsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill all fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        ...messageForm,
        portal_type: 'institutional'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message sent to IMCAC');
      setShowMessageForm(false);
      setMessageForm({ subject: '', message: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Under Review' },
      in_review: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'In Review' },
      approved: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
      incomplete: { bg: 'bg-orange-100 text-orange-700', icon: AlertCircle, label: 'Incomplete' },
      resubmission: { bg: 'bg-purple-100 text-purple-700', icon: AlertCircle, label: 'Resubmission Required' },
    };
    const style = styles[status] || styles.submitted;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm px-3 py-1`}>
        <Icon className="h-3 w-3 mr-1" /> {style.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  const unreadMessages = messages.filter(m => m.direction === 'outgoing' && m.status === 'unread').length;

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="institutional-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Institution Portal</p>
          <h1 className="text-3xl font-bold text-white tracking-tight font-heading">Welcome, {user?.full_name}</h1>
          <p className="text-slate-300 text-sm mt-1">Manage your institutional accreditation applications</p>
          <div className="mt-4 flex gap-2">
            <Badge className="bg-white/10 text-white text-xs px-3 py-1">
              <Mail className="h-3 w-3 mr-1" /> Login: /login/institutional
            </Badge>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-blue-100 text-blue-600">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{applications.length}</p>
                <p className="text-xs text-slate-500">Applications</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-green-100 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{applications.filter(a => a.status === 'approved').length}</p>
                <p className="text-xs text-slate-500">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-amber-100 text-amber-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{applications.filter(a => ['submitted', 'in_review'].includes(a.status)).length}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-purple-100 text-purple-600">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{certificates.length}</p>
                <p className="text-xs text-slate-500">Certificates</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-indigo-100 text-indigo-600">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{unreadMessages}</p>
                <p className="text-xs text-slate-500">New Messages</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="imcac-card">
          <Tabs defaultValue="applications" className="w-full">
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="applications" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <FileText className="h-4 w-4 mr-1" /> Applications
              </TabsTrigger>
              <TabsTrigger value="certificates" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Award className="h-4 w-4 mr-1" /> Certificates
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages {unreadMessages > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs">{unreadMessages}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* Applications Tab */}
            <TabsContent value="applications" className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Your Applications</h3>
                <Button asChild className="bg-[#1B2A4A] text-white rounded-sm text-sm">
                  <a href="/apply/institutional"><FileText className="h-4 w-4 mr-1" /> New Application</a>
                </Button>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Applications Yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Start your institutional accreditation journey</p>
                  <Button asChild className="bg-[#C5A572] text-white rounded-sm">
                    <a href="/apply/institutional">Apply for Accreditation</a>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div key={app.id} className="border border-slate-200 rounded-sm p-4 hover:border-[#C5A572] transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-[#1B2A4A]">{app.institution_name}</h3>
                          <p className="text-sm text-slate-500">{app.registration_number}</p>
                          <p className="text-xs text-slate-400 mt-1">Submitted: {new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                        {getStatusBadge(app.status)}
                      </div>
                      {app.rejection_reason && (
                        <div className="mt-3 pt-3 border-t border-slate-100 bg-red-50 p-2 rounded">
                          <p className="text-xs text-red-600"><strong>Reason:</strong> {app.rejection_reason}</p>
                        </div>
                      )}
                      {app.courses && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs text-slate-500">Courses for Accreditation:</p>
                          <p className="text-sm text-[#1B2A4A]">{app.courses}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates" className="p-5">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Your Certificates</h3>
              {certificates.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Certificates Yet</h3>
                  <p className="text-sm text-slate-500">Certificates will appear here once your accreditation is approved</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {certificates.map((cert, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-sm p-4 bg-gradient-to-r from-[#1B2A4A]/5 to-[#C5A572]/5">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className="bg-green-100 text-green-700 text-xs mb-2">Accreditation Certificate</Badge>
                          <h3 className="text-lg font-semibold text-[#1B2A4A]">{cert.institution_name}</h3>
                          <p className="text-sm text-slate-600 font-mono">{cert.certificate_number}</p>
                          <p className="text-xs text-slate-500 mt-1">Issued: {new Date(cert.issued_at).toLocaleDateString()}</p>
                          <p className="text-xs text-slate-500">Valid: {cert.validity_period}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button size="sm" className="bg-[#1B2A4A] text-white rounded-sm text-xs" asChild>
                            <a href={`/verification?cert=${cert.certificate_number}`} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3 w-3 mr-1" /> Verify
                            </a>
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm text-xs" asChild>
                            <a href={`${API}/certificates/${cert.id}/download`} target="_blank" rel="noreferrer">
                              <Download className="h-3 w-3 mr-1" /> Download
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
                <Button onClick={() => setShowMessageForm(!showMessageForm)} className="bg-[#C5A572] text-white rounded-sm text-sm">
                  <Send className="h-4 w-4 mr-1" /> New Message
                </Button>
              </div>

              {showMessageForm && (
                <Card className="mb-4 border-2 border-[#C5A572]">
                  <CardContent className="p-4">
                    <form onSubmit={sendMessage} className="space-y-3">
                      <div>
                        <Label className="text-xs">Subject</Label>
                        <Input
                          value={messageForm.subject}
                          onChange={e => setMessageForm({...messageForm, subject: e.target.value})}
                          placeholder="Message subject"
                          className="mt-1 rounded-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Message</Label>
                        <textarea
                          value={messageForm.message}
                          onChange={e => setMessageForm({...messageForm, message: e.target.value})}
                          placeholder="Write your message to IMCAC..."
                          className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm"
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="bg-[#1B2A4A] text-white rounded-sm text-sm" disabled={sending}>
                          {sending ? 'Sending...' : 'Send Message'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowMessageForm(false)} className="rounded-sm text-sm">Cancel</Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Messages Yet</h3>
                  <p className="text-sm text-slate-500">Send a message to IMCAC for any queries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`border rounded-sm p-4 ${msg.direction === 'outgoing' ? 'border-[#C5A572] bg-[#C5A572]/5' : 'border-slate-200'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={`text-xs mb-1 ${msg.direction === 'outgoing' ? 'bg-[#C5A572] text-white' : 'bg-blue-100 text-blue-700'}`}>
                            {msg.direction === 'outgoing' ? 'From IMCAC' : 'Sent'}
                          </Badge>
                          <h4 className="text-sm font-semibold text-[#1B2A4A]">{msg.subject}</h4>
                          <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                        </div>
                        <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        {/* Quick Links */}
        <Card className="imcac-card mt-6">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/apply/institutional">New Application</a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/accreditation">Accreditation Guidelines</a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/contact">Contact IMCAC</a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/verification">Verify Certificate</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
